import React, { useState } from 'react';
import { Building2, Users, AlertTriangle, Check, Copy, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CompanyService from '../../services/companyService';
import AuthService from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';
import { SectionHeader } from '../../components/common/SectionHeader';

const inputClass =
  'w-full h-12 px-4 rounded-xl border text-sm transition-all outline-none ' +
  'bg-gray-50 dark:bg-white/5 ' +
  'border-gray-200 dark:border-white/10 ' +
  'text-gray-900 dark:text-white ' +
  'placeholder:text-gray-400 dark:placeholder:text-gray-500 ' +
  'focus:border-[#24299d] focus:ring-2 focus:ring-[#24299d]/20 ' +
  'dark:focus:border-[#5a61e0]';

const Label = ({ children, required }) => (
  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">
    {children} {required && <span className="text-red-400">*</span>}
  </label>
);

const AddCompany = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const currentUser = AuthService.getCurrentUser();

  const emptyForm = {
    name: '', vergiKimlikNo: '', vergiDairesi: '', naceCode: '',
    employeeCount: '', phone: '', address: '', email: '', website: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdCompanyId, setCreatedCompanyId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Tax ID: digits only, max 10
    if (name === 'vergiKimlikNo') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setFormData((p) => ({ ...p, vergiKimlikNo: digits }));
      return;
    }
    // NACE code: digits only, max 6
    if (name === 'naceCode') {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      setFormData((p) => ({ ...p, naceCode: digits }));
      return;
    }
    // Phone: digits only, max 10, masked
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      let masked = '';
      if (digits.length > 0) masked = '(' + digits.slice(0, 3);
      if (digits.length >= 4) masked += ') ' + digits.slice(3, 6);
      if (digits.length >= 7) masked += ' ' + digits.slice(6, 8);
      if (digits.length >= 9) masked += ' ' + digits.slice(8, 10);
      setFormData((p) => ({ ...p, phone: masked }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
    if (error) setError(null);
  };

  const isSubmitDisabled =
    !formData.name.trim() ||
    formData.vergiKimlikNo.length !== 10 ||
    !formData.naceCode ||
    loading;

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    if (!currentUser?.id) {
      toast.error('Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const result = await CompanyService.createCompany({
        userId: currentUser.id,
        name: formData.name.trim(),
        vergiKimlikNo: formData.vergiKimlikNo,
        vergiDairesi: formData.vergiDairesi.trim() || null,
        naceCode: parseInt(formData.naceCode, 10),
        employeeCount: formData.employeeCount ? parseInt(formData.employeeCount, 10) : null,
        phone: formData.phone ? formData.phone.replace(/\D/g, '') : null,
        address: formData.address.trim() || null,
        email: formData.email.trim() || null,
        website: formData.website.trim() || null,
        status: 1,
      });
      if (result.success) {
        setSuccess(true);
        setCreatedCompanyId(result.data?.id || null);
        toast.success(t('company.createSuccess'));
        setFormData(emptyForm);
      } else {
        const msg =
          typeof result.error === 'string'
            ? result.error
            : result.error?.message || 'Şirket oluşturulamadı';
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.message || 'Bir hata oluştu';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyId = () => {
    if (!createdCompanyId) return;
    navigator.clipboard.writeText(createdCompanyId);
    setCopied(true);
    toast.success(t('company.copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white hidden md:block">
          {t('company.addTitle')}
        </h2>
      </div>

      <div className="bg-white dark:bg-[#1C1C1E] rounded-[28px] border border-gray-100 dark:border-white/5 shadow-sm overflow-hidden">
        <div className="p-6 md:p-8 space-y-8">

          {/* Basic Info */}
          <div>
            <SectionHeader icon={Building2} label={t('company.basicInfo')} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Company name */}
              <div className="space-y-1.5">
                <Label required>{t('company.companyName')}</Label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="ABC Teknoloji A.Ş."
                  className={inputClass}
                />
              </div>

              {/* Tax ID */}
              <div className="space-y-1.5">
                <Label required>{t('company.taxId')}</Label>
                <input
                  name="vergiKimlikNo"
                  value={formData.vergiKimlikNo}
                  onChange={handleChange}
                  placeholder="1234567890"
                  inputMode="numeric"
                  className={`${inputClass} ${
                    formData.vergiKimlikNo.length > 0 && formData.vergiKimlikNo.length < 10
                      ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20'
                      : ''
                  }`}
                />
                {formData.vergiKimlikNo.length > 0 && formData.vergiKimlikNo.length < 10 && (
                  <p className="text-[11px] text-red-400">
                    {formData.vergiKimlikNo.length}/10 — 10 haneli olmalıdır
                  </p>
                )}
              </div>

              {/* Tax Office */}
              <div className="space-y-1.5">
                <Label>{t('company.taxOffice')}</Label>
                <input
                  name="vergiDairesi"
                  value={formData.vergiDairesi}
                  onChange={handleChange}
                  placeholder="Kadıköy V.D."
                  className={inputClass}
                />
              </div>

              {/* NACE */}
              <div className="space-y-1.5">
                <Label required>{t('company.naceCode')}</Label>
                <input
                  name="naceCode"
                  value={formData.naceCode}
                  onChange={handleChange}
                  placeholder={t('company.naceCodePlaceholder')}
                  inputMode="numeric"
                  className={inputClass}
                />
                <p className="text-[11px] text-gray-400">{t('company.naceCodeHint')}</p>
              </div>

              {/* Employee count */}
              <div className="space-y-1.5">
                <Label>{t('company.employeeCount')}</Label>
                <input
                  name="employeeCount"
                  type="number"
                  min="0"
                  value={formData.employeeCount}
                  onChange={handleChange}
                  placeholder={t('company.employeeCountPlaceholder')}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <SectionHeader icon={Users} label={t('company.contactInfo')} color="bg-purple-100 dark:bg-purple-900/30" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Phone */}
              <div className="space-y-1.5">
                <Label>{t('company.phone')}</Label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(5xx) xxx xx xx"
                  inputMode="numeric"
                  className={`${inputClass} ${
                    formData.phone.length > 0 && formData.phone.replace(/\D/g, '').length < 10
                      ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20'
                      : ''
                  }`}
                />
                {formData.phone.length > 0 && formData.phone.replace(/\D/g, '').length < 10 && (
                  <p className="text-[11px] text-red-400">10 haneli telefon giriniz</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label>{t('company.email')}</Label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="info@sirket.com"
                  className={inputClass}
                />
              </div>

              {/* Website */}
              <div className="space-y-1.5">
                <Label>{t('company.website')}</Label>
                <input
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://sirket.com"
                  className={inputClass}
                />
              </div>

              {/* Address */}
              <div className="space-y-1.5 md:col-span-3">
                <Label>{t('company.address')}</Label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('company.addressPlaceholder')}
                  rows={3}
                  maxLength={300}
                  className={`${inputClass} h-auto resize-none py-3`}
                />
                <p className="text-[11px] text-gray-400 text-right">{formData.address.length}/300</p>
              </div>
            </div>
          </div>

          {/* Success banner */}
          {success && createdCompanyId && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300 mb-1">
                    {t('company.createSuccess')}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400">
                    {t('company.companyId')}:{' '}
                    <code className="font-mono bg-green-100 dark:bg-green-800/30 px-1 rounded">
                      {createdCompanyId}
                    </code>
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {t('company.uploadHintDetail')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-400 text-xs font-semibold text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-800/20 shrink-0"
                >
                  {copied ? <><Check size={13} /> {t('company.copied')}</> : <><Copy size={13} /> {t('company.copyId')}</>}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {error && (
          <div className="mx-6 md:mx-8 mb-0 flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 rounded-xl px-4 py-3">
            <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-600 dark:text-red-400 font-medium">{error}</p>
          </div>
        )}

        <div className="px-6 md:px-8 py-5 border-t border-gray-100 dark:border-white/5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/edefter/dashboard')}
            disabled={loading}
            className="h-11 px-6 rounded-xl border border-gray-200 dark:border-white/10 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-40"
          >
            {t('company.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="h-11 px-7 rounded-xl bg-[#24299d] text-white text-sm font-semibold shadow-lg shadow-[#24299d]/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 disabled:hover:scale-100 flex items-center gap-2"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? t('company.creating') : t('company.createButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCompany;
